import { Link, type Href } from "expo-router";
import type { PropsWithChildren, ReactNode } from "react";
import { Image, Platform, Pressable, StyleSheet, View } from "react-native";

import { Card, Screen } from "@/design/primitives";
import { Muted, Overline, Text } from "@/design/Text";
import { hoverRingStrong, palette, radius, space } from "@/design/tokens";

export function PublicInfoShell({
  children,
  eyebrow,
  intro,
  title
}: PropsWithChildren<{
  eyebrow: string;
  intro: string;
  title: string;
}>) {
  return (
    <Screen enter="rise">
      <View style={styles.nav}>
        <Link href="/" accessibilityLabel="Recital Master home">
          <View style={styles.brandRow}>
            <Image
              source={require("../../assets/brand-icon.png")}
              style={styles.brandMark}
              accessibilityLabel="Recital Master"
            />
            <Text style={styles.wordmark}>Recital Master</Text>
          </View>
        </Link>
        <Link href="/login" asChild>
          <Pressable
            accessibilityRole="link"
            style={({ hovered, focused }) => [
              styles.openApp,
              (hovered || focused) && styles.openAppLit
            ]}
          >
            <Text variant="smallStrong" color={palette.textSoft}>
              Open app
            </Text>
          </Pressable>
        </Link>
      </View>

      <View style={styles.hero}>
        <Overline>{eyebrow}</Overline>
        <Text variant="hero">{title}</Text>
        <Muted style={styles.intro}>{intro}</Muted>
      </View>

      <Card style={styles.document}>{children}</Card>

      <View style={styles.footer}>
        <Link href={"/privacy" as Href} style={styles.footerLink}>Privacy</Link>
        <View style={styles.footerDot} />
        <Link href={"/support" as Href} style={styles.footerLink}>Support</Link>
        <View style={styles.footerDot} />
        <Link href={"/delete-account" as Href} style={styles.footerLink}>Account deletion</Link>
      </View>
    </Screen>
  );
}

export function InfoSection({ children, title }: PropsWithChildren<{ title: string }>) {
  return (
    <View style={styles.section}>
      <Text variant="title">{title}</Text>
      <View style={styles.sectionCopy}>{children}</View>
    </View>
  );
}

export function InfoParagraph({ children }: { children: ReactNode }) {
  return <Text color={palette.textSoft}>{children}</Text>;
}

export function InfoBullet({ children }: { children: ReactNode }) {
  return (
    <View style={styles.bulletRow}>
      <View style={styles.bullet} />
      <Text color={palette.textSoft} style={styles.bulletCopy}>{children}</Text>
    </View>
  );
}

export function InfoLink({ children, href }: PropsWithChildren<{ href: string }>) {
  return (
    <Link href={href as Href} style={styles.inlineLink}>
      {children}
    </Link>
  );
}

const styles = StyleSheet.create({
  brandMark: {
    height: 28,
    width: 28
  },
  brandRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.md
  },
  bullet: {
    backgroundColor: palette.accent,
    borderRadius: radius.pill,
    height: 5,
    marginTop: 9,
    width: 5
  },
  bulletCopy: {
    flex: 1
  },
  bulletRow: {
    alignItems: "flex-start",
    flexDirection: "row",
    gap: space.md
  },
  document: {
    alignSelf: "center",
    gap: space.xxl,
    maxWidth: 760,
    padding: Platform.select({ web: space.xxl, default: space.xl }),
    width: "100%"
  },
  footer: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.md,
    justifyContent: "center",
    marginTop: space.xxl,
    paddingBottom: space.xl
  },
  footerDot: {
    backgroundColor: palette.faint,
    borderRadius: radius.pill,
    height: 3,
    width: 3
  },
  footerLink: {
    color: palette.muted,
    fontSize: 13
  },
  hero: {
    alignSelf: "center",
    gap: space.sm,
    marginBottom: space.xxl,
    marginTop: Platform.select({ web: space.xxxl, default: space.xxl }),
    maxWidth: 760,
    width: "100%"
  },
  inlineLink: {
    color: palette.accent,
    textDecorationLine: "underline"
  },
  intro: {
    fontSize: 16,
    lineHeight: 24,
    marginTop: space.xs,
    maxWidth: 680
  },
  nav: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: Platform.select({ web: space.md, default: space.sm })
  },
  openApp: {
    backgroundColor: palette.coolWash,
    borderColor: palette.hairline,
    borderRadius: radius.pill,
    borderWidth: 1,
    paddingHorizontal: space.lg,
    paddingVertical: space.sm
  },
  openAppLit: {
    backgroundColor: palette.coolWashHover,
    ...hoverRingStrong
  },
  section: {
    gap: space.md
  },
  sectionCopy: {
    gap: space.md
  },
  wordmark: {
    fontFamily: "PlusJakartaSans_700Bold",
    fontSize: 18,
    letterSpacing: -0.35
  }
});
